import { Button } from '@/common/ui/Form/Button'
import { TokenAmount } from '@/common/ui/TokenAmount'
import { cn } from '@/common/lib/cn/cn'
import { ActiveSubscriptionDetails } from '@/app/entities/payment/ui/ActiveSubscriptionDetails'
import { formatSubscriptionChargeInterval } from '@/app/entities/subscribe/lib/formatSubscriptionPeriod'
import type { TopUpOption } from '../model/useTopUpForm'
import { useTopUpForm } from '../model/useTopUpForm'

type TopUpFormProps = {
    onCancel?: () => void
    onSuccess?: () => void
    className?: string
}

type TopUpOptionCardProps = {
    option: TopUpOption
    optionKey: string
    isActive: boolean
    onSelect: (optionKey: string) => void
}

const TopUpOptionCard = ({ option, optionKey, isActive, onSelect }: TopUpOptionCardProps) => {
    const isSubscription = option.type === 'subscription'
    const chargeIntervalLabel = isSubscription
        ? formatSubscriptionChargeInterval(option.period, option.num_periods)
        : null
    const oldPrice = isSubscription ? option.old_price : null

    return (
        <label
            className={cn(
                'flex flex-col items-center justify-center gap-4 py-[1.5rem] px-8 rounded-[12px] bg-[rgba(255,255,255,0.02)] transition-colors cursor-pointer',
                { 'ring-1 ring-[#be96ff] bg-[rgba(255,255,255,0.05)]': isActive }
            )}
            aria-label={`${option.label} за ${option.price} рублей`}
            tabIndex={0}
            onKeyDown={event => {
                if (event.key !== 'Enter' && event.key !== ' ') return
                event.preventDefault()
                onSelect(optionKey)
            }}
        >
            <input
                type='radio'
                name='topUpOption'
                value={optionKey}
                checked={isActive}
                onChange={() => onSelect(optionKey)}
                className='sr-only'
                aria-checked={isActive}
            />
            <TokenAmount value={option.tokens} />
            <div className='flex flex-col items-center gap-1'>
                {oldPrice != null && (
                    <span className='text-sm font-medium text-[#aba7af] leading-[1.3] line-through'>
                        {oldPrice.toLocaleString('ru-RU')} ₽
                    </span>
                )}
                <div className='text-2xl font-semibold text-white leading-[1.4]'>
                    {option.price.toLocaleString('ru-RU')} ₽
                </div>
            </div>
            <div className='text-xs text-[var(--pixorion-approval)] leading-[1.3] font-medium text-center'>
                {option.label}
            </div>
            {chargeIntervalLabel && (
                <div className='text-xs text-[#aba7af] leading-[1.3] text-center'>{chargeIntervalLabel}</div>
            )}
            {!isSubscription && option.description && (
                <div className='text-xs text-[#aba7af] leading-[1.3] text-center'>{option.description}</div>
            )}
        </label>
    )
}

type TopUpOptionsGridProps = {
    options: TopUpOption[]
    selectedOptionKey: string | null
    getOptionKey: (option: TopUpOption) => string
    onSelect: (optionKey: string) => void
}

const TopUpOptionsGrid = ({ options, selectedOptionKey, getOptionKey, onSelect }: TopUpOptionsGridProps) => (
    <div className='grid grid-cols-1 gap-2 md:grid-cols-3 w-full'>
        {options.map(option => {
            const optionKey = getOptionKey(option)
            return (
                <TopUpOptionCard
                    key={optionKey}
                    option={option}
                    optionKey={optionKey}
                    isActive={selectedOptionKey === optionKey}
                    onSelect={onSelect}
                />
            )
        })}
    </div>
)

export const TopUpForm = ({ onCancel, onSuccess, className }: TopUpFormProps) => {
    const {
        subscriptionOptions,
        tokenOptions,
        options,
        hasActiveSubscription,
        activeSubscription,
        activeSubscriptionPeriod,
        isLoading,
        loadError,
        selectedOptionKey,
        isPaying,
        isReady,
        resolvedError,
        handleOptionSelect,
        handleSubmit,
        getOptionKey,
    } = useTopUpForm({ onSuccess })

    if (isLoading) {
        return (
            <div className={cn('flex flex-col gap-6 w-full', className)}>
                <div className='flex flex-col gap-3'>
                    <div className='h-5 w-32 animate-pulse rounded bg-[rgba(255,255,255,0.05)]' aria-hidden='true' />
                    <div className='grid grid-cols-1 gap-2 md:grid-cols-3 w-full'>
                        {[1, 2].map(item => (
                            <div
                                key={item}
                                className='animate-pulse rounded-[12px] bg-[rgba(255,255,255,0.05)] min-h-[180px]'
                                aria-hidden='true'
                            />
                        ))}
                    </div>
                </div>
                <div className='flex flex-col gap-3'>
                    <div className='h-5 w-40 animate-pulse rounded bg-[rgba(255,255,255,0.05)]' aria-hidden='true' />
                    <div className='grid grid-cols-1 gap-2 md:grid-cols-3 w-full'>
                        {[1, 2, 3].map(item => (
                            <div
                                key={item}
                                className='animate-pulse rounded-[12px] bg-[rgba(255,255,255,0.05)] min-h-[180px]'
                                aria-hidden='true'
                            />
                        ))}
                    </div>
                </div>
            </div>
        )
    }

    if (loadError) {
        return (
            <p className={cn('text-sm text-[#ff8a8a]', className)} role='alert'>
                Не удалось загрузить варианты оплаты.
            </p>
        )
    }

    if (options.length === 0) {
        return <p className={cn('text-sm text-[#aba7af]', className)}>Варианты оплаты временно недоступны.</p>
    }

    return (
        <form className={cn('flex flex-col gap-6 w-full', className)} onSubmit={handleSubmit}>
            {(hasActiveSubscription || subscriptionOptions.length > 0) && (
                <div className='flex flex-col gap-3'>
                    <h4 className='text-base font-medium text-white'>Подписка</h4>
                    {hasActiveSubscription && activeSubscription && (
                        <ActiveSubscriptionDetails
                            plan={activeSubscription.plan}
                            daysRemaining={activeSubscription.days_remaining ?? 0}
                            tokensGranted={activeSubscriptionPeriod?.tokens_granted ?? 0}
                            isAutoRenew={activeSubscription.is_auto_renew}
                            expiresAt={activeSubscription.expires_at}
                            isLoading={isLoading}
                        />
                    )}
                    {subscriptionOptions.length > 0 && (
                        <TopUpOptionsGrid
                            options={subscriptionOptions}
                            selectedOptionKey={selectedOptionKey}
                            getOptionKey={getOptionKey}
                            onSelect={handleOptionSelect}
                        />
                    )}
                </div>
            )}

            {tokenOptions.length > 0 && (
                <div className='flex flex-col gap-3'>
                    <h4 className='text-base font-medium text-white'>Разовая покупка токенов</h4>
                    <TopUpOptionsGrid
                        options={tokenOptions}
                        selectedOptionKey={selectedOptionKey}
                        getOptionKey={getOptionKey}
                        onSelect={handleOptionSelect}
                    />
                </div>
            )}

            {resolvedError && (
                <p className='text-sm text-[#ff8a8a]' role='alert'>
                    {resolvedError}
                </p>
            )}
            <div className='flex items-center justify-end gap-2 w-full'>
                {onCancel && (
                    <Button
                        size='lg'
                        emphasis='accent'
                        variant='ghost'
                        className='px-4'
                        onClick={onCancel}
                        type='button'
                    >
                        Отмена
                    </Button>
                )}
                <Button
                    size='lg'
                    emphasis='accent'
                    className={cn('px-6', {
                        'text-white': selectedOptionKey && isReady && !isPaying,
                        'text-[#aba7af]': !selectedOptionKey || !isReady || isPaying,
                    })}
                    disabled={!selectedOptionKey || !isReady || isPaying}
                    loading={isPaying}
                    type='submit'
                >
                    Оплатить
                </Button>
            </div>
        </form>
    )
}
